// --- src/components/Reconciliation.tsx ---
import React, { useEffect, useState } from 'react';
import { InventoryMap, SupplierMap } from './types';
import { supabaseService } from '../services/supabaseService';
import { LogItem } from './types';
import { Download, History, Clock, Filter } from 'lucide-react';

interface ReconciliationProps {
  inventoryData: InventoryMap;
  supplierData: SupplierMap;
  isAdmin?: boolean;
}

// --- KONFIGURÁCIÓ ---
const LOG_TYPES: Record<string, { label: string; style: string; activeStyle: string }> = {
  SALE: {
    label: 'KASSZA',
    style: 'bg-green-50 text-green-600 border-green-200',
    activeStyle: 'bg-green-600 text-white border-green-600',
  },
  SCRAP: {
    label: 'SELEJT',
    style: 'bg-red-50 text-red-600 border-red-200',
    activeStyle: 'bg-red-600 text-white border-red-600',
  },
  INTERNAL: {
    label: 'SAJÁT',
    style: 'bg-orange-50 text-orange-600 border-orange-200',
    activeStyle: 'bg-orange-500 text-white border-orange-500',
  },
  RETURN: {
    label: 'VISSZÁRU',
    style: 'bg-purple-50 text-purple-600 border-purple-200',
    activeStyle: 'bg-purple-600 text-white border-purple-600',
  },
  COUNT: {
    label: 'LELTÁR',
    style: 'bg-blue-50 text-blue-600 border-blue-200',
    activeStyle: 'bg-blue-600 text-white border-blue-600',
  },
  IMPORT: {
    label: 'IMPORT',
    style: 'bg-indigo-50 text-indigo-600 border-indigo-200',
    activeStyle: 'bg-indigo-600 text-white border-indigo-600',
  },
  EXPORT: {
    label: 'EXPORT',
    style: 'bg-teal-50 text-teal-600 border-teal-200',
    activeStyle: 'bg-teal-600 text-white border-teal-600',
  },
  RESET: {
    label: 'TÖRLÉS',
    style: 'bg-gray-50 text-gray-600 border-gray-200',
    activeStyle: 'bg-gray-600 text-white border-gray-600',
  },
};

const Reconciliation: React.FC<ReconciliationProps> = ({
  inventoryData,
  supplierData,
  isAdmin = false,
}) => {
  const [logs, setLogs] = useState<LogItem[]>([]);

  // ÚJ STATE: Az aktív szűrő ('ALL' az alapértelmezett)
  const [activeFilter, setActiveFilter] = useState<string>('ALL');

  // Amikor változik a szűrő, újratöltjük az adatokat
  useEffect(() => {
    fetchLogs();
  }, [activeFilter]);

  const fetchLogs = async () => {
    const data = await supabaseService.getLogs(activeFilter);
    setLogs(data);
  };

  const totalValue = Object.values(inventoryData).reduce((sum, item) => {
    const price = supplierData[item.ean]?.netPrice || 0;
    return sum + item.quantity * price;
  }, 0);

  const handleExport = async () => {
    const csvContent =
      'EAN,Név,Darab,Beszerzési Ár,Érték\n' +
      Object.values(inventoryData)
        .map((i) => {
          const price = supplierData[i.ean]?.netPrice || 0;
          return `${i.ean},${supplierData[i.ean]?.name || '?'},${i.quantity},${price},${price * i.quantity}`;
        })
        .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `leltar_export_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();

    await supabaseService.logAction(
      'EXPORT',
      `Leltár exportálva (${Object.keys(inventoryData).length} tétel)`
    );
    fetchLogs(); // Frissítjük a listát
  };

  return (
    <div
      className="max-w-5xl mx-auto p-4 md:p-6 space-y-8 min-h-screen"
      style={{ backgroundColor: 'var(--color-background, #f9fafb)' }}
    >
      {/* KÁRTYÁK */}
      <div className={`grid gap-6 ${isAdmin ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
        {/* Leltározott Készletérték - Only visible to admins */}
        {isAdmin && (
          <div className="bg-card p-6 rounded-xl shadow-sm">
            <h3 className="text-gray-500 dark:text-gray-400 font-medium text-sm uppercase">
              Leltározott Készletérték (Beszerzési)
            </h3>
            <p className="text-4xl font-bold text-slate-800 dark:text-gray-100 mt-2">
              {totalValue.toLocaleString()} Ft
            </p>
          </div>
        )}

        {/* Mai Aktivitás - Today's Activity */}
        <div className="bg-card p-6 rounded-xl shadow-sm border border-slate-200 dark:border-gray-700">
          <h3 className="text-gray-500 dark:text-gray-400 font-medium text-sm uppercase">
            Mai Aktivitás
          </h3>
          <p className="text-4xl font-bold text-green-600 dark:text-green-400 mt-2">
            {
              logs.filter((log) => {
                const logDate = new Date(log.created_at).toDateString();
                const today = new Date().toDateString();
                return logDate === today;
              }).length
            }{' '}
            db
          </p>
        </div>

        {/* Összes Megszámolt - Total Counted */}
        <div className="bg-card p-6 rounded-xl shadow-sm border border-slate-200 dark:border-gray-700">
          <h3 className="text-gray-500 dark:text-gray-400 font-medium text-sm uppercase">
            Összes Megszámolt
          </h3>
          <p className="text-4xl font-bold text-blue-600 dark:text-blue-400 mt-2">
            {logs.length} db
          </p>
        </div>
      </div>

      {/* EXPORT */}
      <div className="text-right">
        <button
          onClick={handleExport}
          className="bg-green-600 text-white px-6 py-3 rounded-xl font-bold shadow-lg hover:bg-green-700 transition flex items-center gap-2 ml-auto"
        >
          <Download /> Eredmény Exportálása (CSV)
        </button>
      </div>

      {/* HISTORY / NAPLÓ */}
      <div className="bg-card rounded-xl shadow-sm border border-slate-200 dark:border-gray-700 overflow-hidden">
        {/* FEJLÉC ÉS SZŰRŐK */}
        <div className="bg-slate-50 dark:bg-gray-900 px-6 py-4 border-b border-slate-200 dark:border-gray-700">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <History className="text-slate-500 dark:text-gray-400" />
              <h3 className="font-bold text-slate-700 dark:text-gray-200">Tevékenységnapló</h3>
            </div>

            {/* SZŰRŐ GOMBOK */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setActiveFilter('ALL')}
                className={`px-3 py-1 rounded-full text-xs font-bold border transition ${
                  activeFilter === 'ALL'
                    ? 'bg-slate-800 dark:bg-gray-700 text-white border-slate-800 dark:border-gray-700'
                    : 'bg-card text-slate-500 dark:text-gray-400 border-slate-300 dark:border-gray-600 hover:bg-slate-100 dark:hover:bg-gray-700'
                }`}
              >
                ÖSSZES
              </button>

              {/* Generáljuk a gombokat a LOG_TYPES-ból */}
              {['SALE', 'SCRAP', 'RETURN', 'INTERNAL', 'COUNT', 'IMPORT'].map((type) => {
                const config = LOG_TYPES[type];
                const isActive = activeFilter === type;
                return (
                  <button
                    key={type}
                    onClick={() => setActiveFilter(type)}
                    className={`px-3 py-1 rounded-full text-xs font-bold border transition ${
                      isActive
                        ? config.activeStyle
                        : 'bg-card text-slate-500 dark:text-gray-400 border-slate-200 dark:border-gray-600 hover:bg-slate-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {config.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* LISTA */}
        <div className="divide-y divide-slate-100 dark:divide-gray-700 max-h-[500px] overflow-y-auto">
          {logs.length === 0 ? (
            <div className="p-8 text-center text-slate-400 dark:text-gray-500">
              <Filter className="mx-auto mb-2 opacity-50" size={32} />
              <p className="italic">Nincs megjeleníthető esemény ebben a kategóriában.</p>
              {activeFilter !== 'ALL' && (
                <button
                  onClick={() => setActiveFilter('ALL')}
                  className="text-blue-600 dark:text-blue-400 text-sm mt-2 hover:underline"
                >
                  Vissza az összeshez
                </button>
              )}
            </div>
          ) : (
            logs.map((log) => {
              const typeConfig = LOG_TYPES[log.action_type] || {
                label: log.action_type,
                style:
                  'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600',
                activeStyle: '',
              };

              return (
                <div
                  key={log.id}
                  className="px-4 md:px-6 py-3 flex flex-col md:flex-row justify-between items-start md:items-center hover:bg-slate-50 dark:hover:bg-gray-700 gap-2 transition"
                >
                  <div className="flex items-start md:items-center gap-3 flex-1 min-w-0">
                    {/* Címke */}
                    <span
                      className={`inline-flex items-center justify-center px-2 py-1 rounded-md text-[10px] font-bold border uppercase tracking-wider w-20 shrink-0 text-center shadow-sm ${typeConfig.style}`}
                    >
                      {typeConfig.label}
                    </span>
                    {/* Leírás és felhasználó */}
                    <div className="flex-1 min-w-0">
                      <span className="text-slate-700 dark:text-gray-200 text-sm font-medium break-words block">
                        {log.details}
                      </span>
                      {log.user_name && (
                        <span className="text-xs text-slate-500 dark:text-gray-400 mt-1 block">
                          {log.user_name}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Időpont */}
                  <div className="text-xs text-slate-400 dark:text-gray-500 flex items-center gap-1 md:min-w-[140px] justify-start md:justify-end font-mono shrink-0">
                    <Clock size={12} />
                    <span className="whitespace-nowrap">
                      {new Date(log.created_at).toLocaleString('hu-HU')}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default Reconciliation;
